import React, { useState, useEffect } from 'react';
import { usersApi, tenantsApi, User, UserCreate, UserUpdate, Tenant } from '../services/api';
import { useAuth } from '../store/useStore';

// Helper function to safely convert errors to string
const formatError = (error: any): string => {
  if (!error) return 'Unknown error';

  // If it's already a string, return it
  if (typeof error === 'string') return error;

  // If it's an array (Pydantic validation errors)
  if (Array.isArray(error)) {
    return error.map(e => {
      if (typeof e === 'object' && e.msg) {
        return `${e.loc ? e.loc.join('.') + ': ' : ''}${e.msg}`;
      }
      return JSON.stringify(e);
    }).join('; ');
  }

  // If it's an object with detail property
  if (typeof error === 'object' && error.detail) {
    return formatError(error.detail);
  }

  // Try to stringify the object
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
};

const UsersManager: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [newUser, setNewUser] = useState<UserCreate>({
    username: '',
    email: '',
    password: '',
    full_name: '',
    role: 'tenant_user',
    tenant_id: null,
  });
  const [selectedTenantInfo, setSelectedTenantInfo] = useState<Tenant | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [editUser, setEditUser] = useState<UserUpdate>({
    email: '',
    full_name: '',
    role: 'tenant_user',
    tenant_id: null,
    is_active: true,
  });

  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    fetchUsers();
    fetchTenants();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await usersApi.getAll();
      setUsers(response.data.users || []);
      setError(null);
    } catch (err: any) {
      if (err.response?.status === 401) {
        setError('Authentication required. Please login.');
      } else if (err.response?.status === 403) {
        setError('Access denied. Only administrators can manage users.');
      } else {
        setError(formatError(err.response?.data?.detail || err.message || 'Failed to fetch users'));
      }
      console.error('Users fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const response = await tenantsApi.getAll();
      setTenants(response.data.tenants || []);
    } catch (err: any) {
      console.error('Tenants fetch error:', err);
    }
  };

  const createUser = async () => {
    try {
      await usersApi.create(newUser);
      setNewUser({
        username: '',
        email: '',
        password: '',
        full_name: '',
        role: 'tenant_user',
        tenant_id: null,
      });
      setSelectedTenantInfo(null); // Clear tenant info
      setCreateError(null); // Clear modal error
      setShowCreateModal(false);
      fetchUsers();
    } catch (err: any) {
      // Show error inside modal, not in main view
      setCreateError(formatError(err.response?.data?.detail || err.message || 'Failed to create user'));
    }
  };

  const handleViewUser = (user: User) => {
    setSelectedUser(user);
    setShowViewModal(true);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setEditUser({
      email: user.email,
      full_name: user.full_name,
      role: user.role,
      tenant_id: user.tenant_id,
      is_active: user.is_active,
    });
    setError(null); // Clear any previous errors
    setShowEditModal(true);
  };

  const updateUser = async () => {
    if (!selectedUser) return;
    try {
      await usersApi.update(selectedUser.id, editUser);
      setShowEditModal(false);
      setSelectedUser(null);
      setError(null);
      fetchUsers();
    } catch (err: any) {
      // Don't close modal on error, show error message
      setError(formatError(err.response?.data?.detail || err.message || 'Failed to update user'));
    }
  };

  const handleDeleteUser = (user: User) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const deleteUser = async () => {
    if (!selectedUser) return;
    try {
      await usersApi.delete(selectedUser.id);
      setShowDeleteModal(false);
      setSelectedUser(null);
      fetchUsers();
      setError(null);
    } catch (err: any) {
      setError(formatError(err.response?.data?.detail || err.message || 'Failed to delete user'));
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'bg-purple-100 text-purple-800';
      case 'tenant_admin':
        return 'bg-blue-100 text-blue-800';
      case 'tenant_user':
        return 'bg-green-100 text-green-800';
      case 'api_client':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  };

  const getTenantName = (tenantId: string | null) => {
    if (!tenantId) return 'N/A';
    const tenant = tenants.find(t => t.id === tenantId);
    return tenant ? tenant.company_name : tenantId.slice(0, 8) + '...';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading users...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Users Management</h2>
          <p className="mt-1 text-sm text-gray-600">
            Manage platform users and their permissions
          </p>
          {user && (
            <div className="mt-2 text-sm text-blue-600">
              Logged in as: {user.username} ({user.role})
            </div>
          )}
        </div>
        {isSuperAdmin && (
          <button
            onClick={() => {
              setCreateError(null);
              setShowCreateModal(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            ➕ Create User
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="text-red-400">⚠️</div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
              <button
                onClick={fetchUsers}
                className="mt-2 text-sm text-red-600 hover:text-red-500 underline"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <span className="text-2xl">👥</span>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">{users.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <span className="text-2xl">✅</span>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Active</p>
              <p className="text-2xl font-bold text-green-600">
                {users.filter(u => u.is_active).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <span className="text-2xl">👑</span>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Admins</p>
              <p className="text-2xl font-bold text-purple-600">
                {users.filter(u => u.role === 'super_admin' || u.role === 'tenant_admin').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <span className="text-2xl">🏢</span>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">With Tenant</p>
              <p className="text-2xl font-bold text-blue-600">
                {users.filter(u => u.tenant_id !== null).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">All Users</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tenant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                    <div className="text-4xl mb-2">👥</div>
                    <p>No users found</p>
                  </td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{u.full_name}</p>
                        <p className="text-sm text-gray-500">{u.username}</p>
                        <p className="text-xs text-gray-400">{u.email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(u.role)}`}>
                        {u.role.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {getTenantName(u.tenant_id)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(u.is_active)}`}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleViewUser(u)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        View
                      </button>
                      {isSuperAdmin && (
                        <>
                          <button
                            onClick={() => handleEditUser(u)}
                            className="text-yellow-600 hover:text-yellow-900 mr-3"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteUser(u)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-6 border max-w-2xl shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-medium text-gray-900 mb-4">📝 Create New User</h3>

            {createError && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex">
                  <div className="text-red-400 mr-2">⚠️</div>
                  <div className="flex-1">
                    <p className="text-sm text-red-700">{createError}</p>
                  </div>
                  <button
                    onClick={() => setCreateError(null)}
                    className="text-red-400 hover:text-red-600"
                  >
                    ✕
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Username</label>
                <input
                  type="text"
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter username"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Full Name</label>
                <input
                  type="text"
                  value={newUser.full_name}
                  onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter full name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="user@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Password</label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter password (min. 12 characters)"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Password must be at least 12 characters long
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Role</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value as 'super_admin' | 'tenant_admin' | 'tenant_user' | 'api_client' })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="tenant_user">Tenant User</option>
                  <option value="tenant_admin">Tenant Admin</option>
                  <option value="api_client">API Client</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Tenant (Optional)</label>
                <select
                  value={newUser.tenant_id || ''}
                  onChange={(e) => {
                    const tenantId = e.target.value || null;
                    setNewUser({ ...newUser, tenant_id: tenantId });
                    // Find and set selected tenant info
                    const tenant = tenants.find(t => t.id === tenantId);
                    setSelectedTenantInfo(tenant || null);
                  }}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">No Tenant (System User)</option>
                  {tenants.map(t => (
                    <option key={t.id} value={t.id}>{t.company_name} ({t.ruc})</option>
                  ))}
                </select>
              </div>

              {/* Tenant Information Panel */}
              {selectedTenantInfo && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-blue-900">🏢 Información del Tenant</h4>
                    {selectedTenantInfo.has_digital_certificate && selectedTenantInfo.clave_sol_usuario ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        ✓ Listo para Facturar
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        ⚠️ Configuración Incompleta
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-gray-600 font-medium">Empresa:</p>
                      <p className="text-gray-900">{selectedTenantInfo.company_name}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 font-medium">RUC:</p>
                      <p className="text-gray-900 font-mono">{selectedTenantInfo.ruc}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 font-medium">Ambiente:</p>
                      <p className="text-gray-900">
                        {selectedTenantInfo.environment === 'production' ? '🟢 Producción' : '🟡 Beta'}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600 font-medium">Estado:</p>
                      <p className="text-gray-900">
                        {selectedTenantInfo.status === 'active' ? '✅ Activo' : '❌ Inactivo'}
                      </p>
                    </div>
                  </div>

                  <div className="border-t border-blue-200 pt-3 space-y-2">
                    <h5 className="text-xs font-semibold text-blue-900 uppercase">Estado de Configuración:</h5>

                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-700">Certificado Digital:</span>
                      {selectedTenantInfo.has_digital_certificate ? (
                        <span className="text-green-600 font-medium">✓ Configurado</span>
                      ) : (
                        <span className="text-red-600 font-medium">✗ Pendiente</span>
                      )}
                    </div>

                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-700">Clave SOL:</span>
                      {selectedTenantInfo.clave_sol_usuario ? (
                        <span className="text-green-600 font-medium">✓ Configurado</span>
                      ) : (
                        <span className="text-red-600 font-medium">✗ Pendiente</span>
                      )}
                    </div>

                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-700">Dirección Fiscal:</span>
                      {selectedTenantInfo.direccion_fiscal ? (
                        <span className="text-green-600 font-medium">✓ Configurado</span>
                      ) : (
                        <span className="text-yellow-600 font-medium">⚠ Pendiente</span>
                      )}
                    </div>

                    {selectedTenantInfo.certificate_ruc && selectedTenantInfo.ruc !== selectedTenantInfo.certificate_ruc && (
                      <div className="mt-2 bg-red-50 border border-red-200 rounded p-2">
                        <p className="text-xs text-red-700">
                          ⚠️ <strong>Advertencia:</strong> El RUC del certificado ({selectedTenantInfo.certificate_ruc})
                          no coincide con el RUC del tenant ({selectedTenantInfo.ruc})
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="bg-white border border-blue-200 rounded p-3 mt-2">
                    <p className="text-xs text-gray-700">
                      <strong>ℹ️ Info:</strong> El usuario heredará el acceso a los certificados y configuración
                      de este tenant. Puede comenzar a facturar inmediatamente si la configuración está completa.
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end pt-6 space-x-3 border-t mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setCreateError(null);
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={createUser}
                disabled={!newUser.username || !newUser.email || !newUser.password || !newUser.full_name}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                ✅ Create User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View User Modal */}
      {showViewModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-6 border max-w-2xl shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-medium text-gray-900 mb-6">👤 User Details</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">ID</label>
                <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-2 rounded font-mono">{selectedUser.id}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Username</label>
                  <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-2 rounded">{selectedUser.username}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Full Name</label>
                  <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-2 rounded">{selectedUser.full_name}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500">Email</label>
                <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-2 rounded">{selectedUser.email}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Role</label>
                  <div className="mt-1">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(selectedUser.role)}`}>
                      {selectedUser.role.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Status</label>
                  <div className="mt-1">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedUser.is_active)}`}>
                      {selectedUser.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500">Tenant</label>
                <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-2 rounded">{getTenantName(selectedUser.tenant_id)}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500">Permissions</label>
                <div className="mt-1 bg-gray-50 p-2 rounded">
                  {selectedUser.permissions && selectedUser.permissions.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {selectedUser.permissions.map((perm, idx) => (
                        <span key={idx} className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-100 text-blue-800">
                          {perm}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-gray-500 text-sm">No permissions</span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Created At</label>
                  <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-2 rounded">
                    {new Date(selectedUser.created_at).toLocaleString()}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Last Login</label>
                  <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-2 rounded">
                    {selectedUser.last_login ? new Date(selectedUser.last_login).toLocaleString() : 'Never'}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end pt-6 border-t mt-6">
              <button
                onClick={() => setShowViewModal(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-6 border max-w-2xl shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-medium text-gray-900 mb-4">✏️ Edit User: {selectedUser.username}</h3>

            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex">
                  <div className="text-red-400 mr-2">⚠️</div>
                  <div className="flex-1">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                  <button
                    onClick={() => setError(null)}
                    className="text-red-400 hover:text-red-600"
                  >
                    ✕
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Full Name <span className="text-gray-500">(Nombre completo del usuario)</span>
                </label>
                <input
                  type="text"
                  value={editUser.full_name}
                  onChange={(e) => setEditUser({ ...editUser, full_name: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Juan Pérez García"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Email <span className="text-red-500">*</span> <span className="text-gray-500">(Debe ser formato email: usuario@dominio.com)</span>
                </label>
                <input
                  type="email"
                  value={editUser.email}
                  onChange={(e) => setEditUser({ ...editUser, email: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="usuario@example.com"
                />
                <p className="mt-1 text-xs text-gray-500">
                  ⚠️ El campo Email debe tener formato válido (contener @ y .)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Password (leave empty to keep current)</label>
                <input
                  type="password"
                  value={editUser.password || ''}
                  onChange={(e) => setEditUser({ ...editUser, password: e.target.value || undefined })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter new password (optional)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Role</label>
                <select
                  value={editUser.role}
                  onChange={(e) => setEditUser({ ...editUser, role: e.target.value as 'super_admin' | 'tenant_admin' | 'tenant_user' | 'api_client' })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="tenant_user">Tenant User</option>
                  <option value="tenant_admin">Tenant Admin</option>
                  <option value="api_client">API Client</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Tenant</label>
                <select
                  value={editUser.tenant_id || ''}
                  onChange={(e) => setEditUser({ ...editUser, tenant_id: e.target.value || null })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">No Tenant (System User)</option>
                  {tenants.map(t => (
                    <option key={t.id} value={t.id}>{t.company_name} ({t.ruc})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editUser.is_active}
                    onChange={(e) => setEditUser({ ...editUser, is_active: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700">Active</span>
                </label>
              </div>
            </div>

            <div className="flex items-center justify-end pt-6 space-x-3 border-t mt-6">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setError(null);
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={updateUser}
                className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors"
              >
                💾 Update User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Modal */}
      {showDeleteModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full">
                <span className="text-red-600 text-2xl">⚠️</span>
              </div>

              <h3 className="text-lg font-medium text-gray-900 text-center mt-4 mb-4">
                Delete User
              </h3>

              <p className="text-sm text-gray-500 text-center mb-4">
                Are you sure you want to delete "{selectedUser.username}"?
                This action cannot be undone.
              </p>

              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4">
                <p className="text-sm text-yellow-800">
                  <strong>Warning:</strong> This will permanently delete the user and revoke all access.
                </p>
              </div>

              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={deleteUser}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  Delete User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersManager;
